import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Calendar,
  DollarSign,
  AlertCircle,
  FileText,
  MoreHorizontal,
  Edit,
  Trash2,
  TrendingUp,
  RefreshCw,
  Search,
} from 'lucide-react';
import paymentService from '../../services/paymentService';
import ContractService from '../../services/contractService';
import PageHeader from '../common/PageHeader';
import PageBreadcrumb from '../common/PageBreadcrumb';
import DataTable from '../common/DataTable'; // Using your custom DataTable
import ConfirmationModal from '../common/ConfirmationModal'; // Using your custom Modal
import { useToast, ToastContainer } from '../common/Toast'; // Using your custom Toast

// --- Custom Dropdown for Row Actions (Matches your reference) ---
const ActionDropdown = ({ payment, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => setIsOpen(false);

    const handleKey = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    // capture scroll from any scrollable ancestor
    document.addEventListener('scroll', handleScroll, true);
    document.addEventListener('keydown', handleKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className="inline-flex items-center justify-center w-8 h-8 border border-gray-200 bg-white text-gray-500 rounded hover:bg-gray-50 transition-colors shadow-sm"
        onClick={() => setIsOpen(!isOpen)}
      >
        <MoreHorizontal size={16} />
      </button>
      {isOpen && (
        <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-40 origin-top-right">
          <ul className="py-1 m-0 list-none">
            <li>
              <button
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => {
                  setIsOpen(false);
                  onEdit(payment);
                }}
              >
                <Edit size={14} className="mr-2" /> Edit
              </button>
            </li>
            <li className="border-t border-gray-100">
              <button
                className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                onClick={() => {
                  setIsOpen(false);
                  onDelete(payment);
                }}
              >
                <Trash2 size={14} className="mr-2" /> Delete
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

const PaymentManagement = () => {
  const { success, error: showError, toasts, removeToast } = useToast();

  // State
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ totalPaid: 0, outstanding: 0 });

  // Selection State
  const [contracts, setContracts] = useState([]);
  const [selectedContractId, setSelectedContractId] = useState('');

  // Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);

  // Pagination State for DataTable
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  // 1. Fetch Contracts
  useEffect(() => {
    const fetchContracts = async () => {
      try {
        const response = await ContractService.getContracts({ limit: 100 });
        const list = response.data?.contracts || response.data || [];
        setContracts(list);
        if (list.length > 0 && !selectedContractId) {
          setSelectedContractId(list[0]._id);
        }
      } catch (err) {
        console.error('Contract load error', err);
      }
    };
    fetchContracts();
  }, []);

  // 2. Fetch Payments
  const fetchPayments = useCallback(async () => {
    if (!selectedContractId) return;
    setLoading(true);
    try {
      const response = await paymentService.getPayments({
        contractId: selectedContractId,
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
      });

      if (response.success) {
        setPayments(response.data.payments || []);
        setStats({
          totalPaid: response.data.totalPaid || 0,
          outstanding: response.data.outstanding || 0,
        });
        // ONLY update pagination if the total actually changed to avoid loops
        setPagination((prev) => ({
          ...prev,
          totalItems: response.data.pagination?.total || 0,
          totalPages: response.data.pagination?.pages || 1,
        }));
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
      // Don't call showError here if it causes a re-render loop
    } finally {
      setLoading(false);
    }
  }, [selectedContractId, pagination.currentPage, pagination.itemsPerPage]);

  useEffect(() => {
    if (selectedContractId) fetchPayments();
  }, [fetchPayments, selectedContractId]);

  const navigate = useNavigate();

  // Handlers
  const handleCreate = () => {
    if (!selectedContractId) {
      showError('Please select a contract first.');
      return;
    }
    navigate('/finance/payments/create', { state: { contractId: selectedContractId } });
  };

  const handleEdit = (payment) => {
    navigate(`/finance/payments/edit/${payment._id || payment.id}`);
  };

  const handleDeleteClick = (payment) => {
    setPaymentToDelete(payment);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!paymentToDelete) return;
    try {
      const targetId = paymentToDelete._id || paymentToDelete.id;
      await paymentService.deletePayment(targetId);

      success('Payment deleted successfully');
      fetchPayments();
    } catch (error) {
      showError('Failed to delete payment');
    } finally {
      setShowDeleteModal(false);
      setPaymentToDelete(null);
    }
  };

  // --- Data Table Columns ---
  const columns = [
    {
      key: 'paymentId',
      label: 'Reference / ID',
      render: (value, record) => (
        <div>
          <div className="font-semibold text-gray-800">{record.paymentId}</div>
          <div className="text-xs text-gray-500">
            {record.reference || 'No Ref'}
          </div>
        </div>
      ),
    },
    {
      key: 'paymentType',
      label: 'Type',
      render: (value) => (
        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium border border-gray-200">
          {value}
        </span>
      ),
    },
    {
      key: 'amount',
      label: 'Amount',
      render: (value, record) => (
        <div className="font-medium text-gray-900">
          {record.currency} {record.amount?.toLocaleString()}
        </div>
      ),
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      render: (value) => (value ? new Date(value).toLocaleDateString() : '-'),
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => {
        const config = {
          Paid: { bg: '#d1fae5', color: '#10b981', text: 'Paid' },
          Pending: { bg: '#fef3c7', color: '#f59e0b', text: 'Pending' },
          Overdue: { bg: '#fee2e2', color: '#ef4444', text: 'Overdue' },
        }[value] || { bg: '#f3f4f6', color: '#6b7280', text: value };

        return (
          <span
            className="badge"
            style={{
              backgroundColor: config.bg,
              color: config.color,
              border: `1px solid ${config.color}20`,
              borderRadius: '12px',
              padding: '4px 8px',
              fontSize: '12px',
              fontWeight: '500',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <div
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: config.color,
              }}
            />
            {config.text}
          </span>
        );
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, record) => (
        <ActionDropdown
          payment={record}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
        />
      ),
    },
  ];

  // Header Actions
  const headerActions = [
    {
      label: '',
      icon: <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />,
      onClick: fetchPayments,
      variant: 'outline',
      tooltip: 'Refresh payments',
    },
    {
      label: 'Create Payment',
      icon: <Plus size={18} />,
      onClick: handleCreate,
      variant: 'primary',
      tooltip: 'Add new payment record',
    },
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="Payment Management"
        description="Track contract milestones, payments, and financial status"
        actions={headerActions}
      />

      <div className="page-container w-full max-w-full">
        {/* --- Contract Filter Bar --- */}
        <div className="bg-white border border-[var(--border-color)] rounded-lg p-4 mb-6 flex items-center gap-4 shadow-sm">
          <div className="p-2 bg-blue-50 rounded text-blue-600">
            <FileText size={20} />
          </div>
          <div className="flex-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">
              Active Contract
            </label>
            <select
              className="block w-full max-w-md border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2"
              value={selectedContractId}
              onChange={(e) => setSelectedContractId(e.target.value)}
            >
              <option value="">-- Select a Contract --</option>
              {contracts.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.title}
                </option>
              ))}
            </select>
          </div>
          {selectedContractId && (
            <div className="text-sm text-gray-500 border-l pl-4">
              Showing finance data for{' '}
              <strong>
                {contracts.find((c) => c._id === selectedContractId)?.title}
              </strong>
            </div>
          )}
        </div>

        {/* --- KPI Cards (Matches Reference Styling) --- */}
        <div className="page-stats-grid grid-modern-4 w-full gap-6 mb-8 grid grid-cols-1 md:grid-cols-3">
          {/* Total Paid */}
          <div className="relative border border-[var(--border-color)] bg-white rounded-lg overflow-hidden">
            <div className="h-1 w-full bg-emerald-500" />
            <div className="relative flex items-center gap-3 p-4">
              <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-emerald-600">
                <TrendingUp size={14} /> Paid
              </div>
              <div className="flex h-11 w-11 items-center justify-center bg-emerald-50 rounded-lg">
                <DollarSign size={20} className="text-emerald-600" />
              </div>
              <div>
                <div className="text-[20px] font-bold text-gray-900">
                  ${stats.totalPaid?.toLocaleString()}
                </div>
                <div className="mt-1 text-[13px] text-gray-500">
                  Total Amount Paid
                </div>
              </div>
            </div>
          </div>

          {/* Outstanding */}
          <div className="relative border border-[var(--border-color)] bg-white rounded-lg overflow-hidden">
            <div className="h-1 w-full bg-amber-500" />
            <div className="relative flex items-center gap-3 p-4">
              <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-amber-600">
                <AlertCircle size={14} /> Pending
              </div>
              <div className="flex h-11 w-11 items-center justify-center bg-amber-50 rounded-lg">
                <DollarSign size={20} className="text-amber-600" />
              </div>
              <div>
                <div className="text-[20px] font-bold text-gray-900">
                  ${stats.outstanding?.toLocaleString()}
                </div>
                <div className="mt-1 text-[13px] text-gray-500">
                  Outstanding Amount
                </div>
              </div>
            </div>
          </div>

          {/* Next Payment */}
          <div className="relative border border-[var(--border-color)] bg-white rounded-lg overflow-hidden">
            <div className="h-1 w-full bg-blue-500" />
            <div className="relative flex items-center gap-3 p-4">
              <div className="flex h-11 w-11 items-center justify-center bg-blue-50 rounded-lg">
                <Calendar size={20} className="text-blue-600" />
              </div>
              <div>
                <div className="text-[20px] font-bold text-gray-900">
                  {/* Logic to find next due date */}
                  {payments.find((p) => p.status === 'Pending')?.dueDate
                    ? new Date(
                        payments.find((p) => p.status === 'Pending').dueDate,
                      ).toLocaleDateString()
                    : '-'}
                </div>
                <div className="mt-1 text-[13px] text-gray-500">
                  Next Payment Due
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* --- Data Table --- */}
        <div className="relative overflow-visible bg-white rounded-lg shadow-sm border border-gray-200">
          <DataTable
            title="Payment Schedule"
            columns={columns}
            data={payments}
            loading={loading}
            pagination={pagination}
            onPaginationChange={setPagination}
            emptyMessage={
              !selectedContractId
                ? 'Please select a contract to view payments.'
                : 'No payments found for this contract.'
            }
            emptyIcon={<DollarSign size={48} className="text-gray-300" />}
            showActions={false} // We handled actions in columns
          />
        </div>

        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={confirmDelete}
          title="Delete Payment"
          message="Are you sure you want to delete this payment record? This action cannot be undone."
          confirmText="Delete Payment"
          variant="danger"
        />

        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </>
  );
};

export default PaymentManagement;
